import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import type { ApiKeyStatus, Category, ImportReport, PendingTransaction } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { Button, Card, Dot, SectionTitle, Spinner } from '../components/ui';
import { Sheet } from '../components/Sheet';
import { ThemeToggle } from '../components/ThemeToggle';

/**
 * Stessa palette di 16 colori usata dal server per l'assegnazione
 * automatica (server/src/lib/categoryNormalizer.ts) — duplicata qui perche'
 * client e server sono due workspace npm separati, senza un pacchetto
 * condiviso per 16 stringhe fisse.
 */
const CATEGORY_PALETTE = [
  '#2563eb',
  '#16a34a',
  '#ea580c',
  '#9333ea',
  '#0891b2',
  '#dc2626',
  '#ca8a04',
  '#4f46e5',
  '#059669',
  '#db2777',
  '#0284c7',
  '#65a30d',
  '#e11d48',
  '#7c3aed',
  '#0d9488',
  '#d97706',
];

/** Categorie, importazione, account: tutto quello che non serve ogni giorno. */
export function AltroPage() {
  const { user, logout } = useAuth();

  return (
    <div className="flex flex-col gap-5">
      <PendingSection />
      <CategoriesSection />
      <ImportSection />
      <AutomationSection />

      <section>
        <SectionTitle>Aspetto</SectionTitle>
        <Card>
          <ThemeToggle />
        </Card>
      </section>

      <section>
        <SectionTitle>Account</SectionTitle>
        <Card className="flex items-center gap-3">
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{user?.name ?? user?.email}</span>
            {user?.name && <span className="block truncate text-xs text-muted">{user.email}</span>}
          </span>
          <Button variant="ghost" onClick={() => void logout()}>
            Esci
          </Button>
        </Card>
      </section>
    </div>
  );
}

/**
 * Rinomina, unisci, archivia: la manutenzione che il foglio non permetteva.
 *
 * Le azioni stanno in un foglio che si apre toccando la categoria, non come
 * pulsanti in riga: su un telefono tre pulsanti accanto al nome lo
 * schiacciano fino a farlo sparire, e le categorie qui sono 75.
 */
function CategoriesSection() {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Category | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: categories, isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/categories'),
  });

  const normalized = query.trim().toLowerCase();
  const visible = (categories ?? []).filter((c) => c.name.toLowerCase().includes(normalized));

  if (isLoading) return <Spinner />;

  return (
    <section>
      <SectionTitle
        action={
          <button type="button" onClick={() => setShowCreate(true)} className="text-xs text-accent">
            Nuova categoria
          </button>
        }
      >
        Categorie ({categories?.length ?? 0})
      </SectionTitle>
      <Card className="flex flex-col gap-2 p-3">
        {error && (
          <p
            role="alert"
            className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {error}
          </p>
        )}

        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Cerca categoria"
          aria-label="Cerca categoria"
          className="w-full rounded-[var(--radius-control)] bg-surface-2 px-3 py-2.5 text-sm"
        />

        <div className="-mx-1 max-h-96 overflow-y-auto">
          {visible.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                setSelected(c);
                setError(null);
              }}
              className="flex w-full items-center gap-2.5 rounded-[var(--radius-control)] px-3 py-2.5 text-left transition active:bg-surface-2"
            >
              <Dot color={c.color} />
              <span
                className={`min-w-0 flex-1 truncate text-sm ${c.archived ? 'text-muted line-through' : ''}`}
              >
                {c.name}
              </span>
              <span className="tnum text-xs text-muted">{c.transactionCount}</span>
              <span aria-hidden="true" className="text-muted">
                ›
              </span>
            </button>
          ))}
          {visible.length === 0 && (
            <p className="px-3 py-4 text-sm text-muted">Nessun risultato.</p>
          )}
        </div>
      </Card>

      {selected && (
        <CategoryActions
          category={selected}
          categories={categories ?? []}
          onClose={() => setSelected(null)}
          onError={setError}
          onDone={async () => {
            await queryClient.invalidateQueries();
            setSelected(null);
          }}
        />
      )}

      {showCreate && (
        <NewCategorySheet
          onClose={() => setShowCreate(false)}
          onError={setError}
          onDone={async () => {
            await queryClient.invalidateQueries({ queryKey: ['categories'] });
            setShowCreate(false);
          }}
        />
      )}
    </section>
  );
}

/** Azioni su una categoria, in un foglio dedicato. */
function CategoryActions({
  category,
  categories,
  onClose,
  onDone,
  onError,
}: {
  category: Category;
  categories: Category[];
  onClose: () => void;
  onDone: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState(category.name);
  const [mergeTarget, setMergeTarget] = useState('');

  const fail = (err: unknown) =>
    onError(err instanceof ApiError ? err.message : 'Operazione non riuscita');

  const rename = useMutation({
    mutationFn: () => api.patch(`/categories/${category.id}`, { name }),
    onSuccess: onDone,
    onError: fail,
  });

  const merge = useMutation({
    mutationFn: () => api.post(`/categories/${category.id}/merge`, { targetId: mergeTarget }),
    onSuccess: onDone,
    onError: fail,
  });

  const archive = useMutation({
    mutationFn: () => api.patch(`/categories/${category.id}`, { archived: !category.archived }),
    onSuccess: onDone,
    onError: fail,
  });

  return (
    <Sheet open onClose={onClose} labelledBy="category-actions-title">
      <div className="flex flex-col gap-4 px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <h3 id="category-actions-title" className="flex items-center gap-2 text-base font-semibold">
          <Dot color={category.color} />
          {category.name}
          <span className="tnum ml-auto text-sm font-normal text-muted">
            {category.transactionCount} movimenti
          </span>
        </h3>

        <label className="text-sm">
          <span className="mb-1 block text-muted">Nome</span>
          <div className="flex gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-[var(--radius-control)] bg-surface-2 px-3 py-2.5"
            />
            <Button onClick={() => rename.mutate()} disabled={rename.isPending || !name.trim()}>
              Salva
            </Button>
          </div>
        </label>

        <label className="text-sm">
          <span className="mb-1 block text-muted">Unisci in un altra categoria</span>
          <div className="flex gap-2">
            <select
              value={mergeTarget}
              onChange={(e) => setMergeTarget(e.target.value)}
              className="w-full rounded-[var(--radius-control)] bg-surface-2 px-3 py-2.5"
            >
              <option value="">Scegli categoria</option>
              {categories
                .filter((c) => c.id !== category.id)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
            <Button onClick={() => merge.mutate()} disabled={!mergeTarget || merge.isPending}>
              Unisci
            </Button>
          </div>
          <span className="mt-1 block text-xs text-muted">
            I {category.transactionCount} movimenti passano alla categoria scelta.
          </span>
        </label>

        <Button variant="ghost" onClick={() => archive.mutate()} disabled={archive.isPending}>
          {category.archived ? 'Ripristina categoria' : 'Archivia categoria'}
        </Button>
      </div>
    </Sheet>
  );
}

/** Crea una categoria in anticipo, senza passare per l'inserimento di un movimento. */
function NewCategorySheet({
  onClose,
  onDone,
  onError,
}: {
  onClose: () => void;
  onDone: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState('');
  const [color, setColor] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post<Category>('/categories', { name: name.trim(), color: color ?? undefined }),
    onSuccess: onDone,
    onError: (err: unknown) => {
      const message = err instanceof ApiError ? err.message : 'Operazione non riuscita';
      setLocalError(message);
      onError(message);
    },
  });

  return (
    <Sheet open onClose={onClose} labelledBy="new-category-title">
      <div className="flex flex-col gap-4 px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
        <h3 id="new-category-title" className="text-base font-semibold">
          Nuova categoria
        </h3>

        {localError && (
          <p
            role="alert"
            className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {localError}
          </p>
        )}

        <label className="text-sm">
          <span className="mb-1 block text-muted">Nome</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-[var(--radius-control)] bg-surface-2 px-3 py-2.5"
          />
        </label>

        <div className="text-sm">
          <span className="mb-2 block text-muted">Colore</span>
          <div className="flex flex-wrap gap-2.5" role="group" aria-label="Colore">
            {CATEGORY_PALETTE.map((swatch) => (
              <button
                key={swatch}
                type="button"
                onClick={() => setColor(swatch)}
                aria-label={swatch}
                aria-pressed={color === swatch}
                className={`h-8 w-8 rounded-full transition ${
                  color === swatch ? 'ring-2 ring-accent ring-offset-2 ring-offset-surface' : ''
                }`}
                style={{ background: swatch }}
              />
            ))}
          </div>
        </div>

        <Button onClick={() => create.mutate()} disabled={create.isPending || !name.trim()}>
          Salva
        </Button>
      </div>
    </Sheet>
  );
}

/** Caricamento CSV/XLSX: prima l'anteprima, poi la conferma. */
function ImportSection() {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: ({ file: f, commit }: { file: File; commit: boolean }) =>
      api.upload<ImportReport>(`/import?commit=${commit}`, f),
    onSuccess: async (report) => {
      setPreview(report);
      setError(null);
      if (report.committed) {
        await queryClient.invalidateQueries();
        setFile(null);
        if (inputRef.current) inputRef.current.value = '';
      }
    },
    onError: (err) => {
      setPreview(null);
      setError(err instanceof ApiError ? err.message : 'Importazione non riuscita');
    },
  });

  return (
    <section>
      <SectionTitle>Importa</SectionTitle>
      <Card className="flex flex-col gap-3">
        <p className="text-xs text-muted">
          Accetta il foglio Google esportato (una scheda per mese) oppure un CSV con le colonne
          Data, Categoria, Spesa, Guadagno.
        </p>

        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xlsm"
          aria-label="File da importare"
          onChange={(e) => {
            const selected = e.target.files?.[0] ?? null;
            setFile(selected);
            setPreview(null);
            setError(null);
            if (selected) run.mutate({ file: selected, commit: false });
          }}
          className="text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-surface-2 file:px-3 file:py-2 file:text-sm"
        />

        {error && (
          <p
            role="alert"
            className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {error}
          </p>
        )}

        {preview && (
          <div className="rounded-[var(--radius-control)] bg-surface-2 p-3 text-sm">
            {preview.committed ? (
              <p className="font-medium text-success">
                Importati {preview.transactionsInserted} movimenti.
              </p>
            ) : (
              <>
                <p className="font-medium">
                  Anteprima: {preview.transactionsInserted} movimenti, {preview.budgetsInserted}{' '}
                  budget.
                </p>
                {preview.categoriesCreated.length > 0 && (
                  <p className="mt-1 text-xs text-muted">
                    Nuove categorie: {preview.categoriesCreated.join(', ')}
                  </p>
                )}
              </>
            )}

            {preview.warnings.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-muted">
                  {preview.warnings.length} avvisi
                </summary>
                <ul className="mt-1 max-h-40 overflow-y-auto text-xs text-muted">
                  {preview.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              </details>
            )}

            {!preview.committed && file && (
              <Button
                className="mt-3"
                onClick={() => run.mutate({ file, commit: true })}
                disabled={run.isPending}
              >
                {run.isPending ? 'Importo...' : 'Conferma importazione'}
              </Button>
            )}
          </div>
        )}
      </Card>
    </section>
  );
}

interface ApiKeyGenerateResponse {
  key: string;
  prefix: string;
}

/** Chiave API per automazioni esterne, es. una Shortcut iOS su Apple Pay. */
function AutomationSection() {
  const queryClient = useQueryClient();
  const [newKey, setNewKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);

  const { data: status, isLoading } = useQuery({
    queryKey: ['api-key'],
    queryFn: () => api.get<ApiKeyStatus | null>('/account/api-key'),
  });

  const fail = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : 'Operazione non riuscita');

  const generate = useMutation({
    mutationFn: () => api.post<ApiKeyGenerateResponse>('/account/api-key'),
    onSuccess: async (res) => {
      setNewKey(res.key);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['api-key'] });
    },
    onError: fail,
  });

  const revoke = useMutation({
    mutationFn: () => api.delete('/account/api-key'),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['api-key'] });
    },
    onError: fail,
  });

  if (isLoading) return <Spinner />;

  return (
    <section>
      <SectionTitle>Automazione</SectionTitle>
      <Card className="flex flex-col gap-3">
        <p className="text-xs text-muted">
          Usa una chiave API per registrare movimenti da un automatismo esterno, ad esempio una
          Shortcut iOS attivata da un pagamento Apple Pay: richiesta POST a
          /api/automation/apple-pay con l&apos;header X-Api-Key e il corpo {'{ amount, category }'}.
        </p>

        {error && (
          <p
            role="alert"
            className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {error}
          </p>
        )}

        {status ? (
          <div className="flex items-center justify-between gap-3 rounded-[var(--radius-control)] bg-surface-2 px-3 py-2.5 text-sm">
            <span>
              Chiave attiva ({status.prefix}…)
              <span className="block text-xs text-muted">
                Attiva dal {new Date(status.createdAt).toLocaleDateString('it-IT')}
                {status.lastUsedAt && (
                  <> · ultimo utilizzo {new Date(status.lastUsedAt).toLocaleString('it-IT')}</>
                )}
              </span>
            </span>
            <Button
              variant="danger"
              onClick={() => setShowRevokeConfirm(true)}
              disabled={revoke.isPending}
            >
              Revoca
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted">Nessuna chiave attiva.</p>
        )}

        <Button onClick={() => generate.mutate()} disabled={generate.isPending}>
          {status ? 'Genera nuova chiave' : 'Genera chiave'}
        </Button>
      </Card>

      {showRevokeConfirm && (
        <Sheet open onClose={() => setShowRevokeConfirm(false)} labelledBy="revoke-api-key-title">
          <div className="flex flex-col gap-4 px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <h3 id="revoke-api-key-title" className="text-base font-semibold">
              Revocare la chiave API?
            </h3>
            <p className="text-sm text-muted">
              Il Shortcut smetterà di funzionare finché non lo aggiorni con una chiave nuova.
            </p>
            <Button
              variant="danger"
              onClick={() => {
                revoke.mutate();
                setShowRevokeConfirm(false);
              }}
              disabled={revoke.isPending}
            >
              Revoca chiave
            </Button>
            <Button variant="ghost" onClick={() => setShowRevokeConfirm(false)}>
              Annulla
            </Button>
          </div>
        </Sheet>
      )}

      {newKey && (
        <Sheet open onClose={() => setNewKey(null)} labelledBy="new-api-key-title">
          <div className="flex flex-col gap-4 px-4 pt-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
            <h3 id="new-api-key-title" className="text-base font-semibold">
              Nuova chiave API
            </h3>
            <p className="text-sm text-muted">Copiala ora: non verrà più mostrata per intero.</p>
            <code className="break-all rounded-[var(--radius-control)] bg-surface-2 px-3 py-2.5 text-sm">
              {newKey}
            </code>
            <Button onClick={() => void navigator.clipboard.writeText(newKey)}>Copia</Button>
            <Button variant="ghost" onClick={() => setNewKey(null)}>
              Fatto
            </Button>
          </div>
        </Sheet>
      )}
    </section>
  );
}

/** Movimenti creati da un automatismo, in attesa che l'utente scelga la categoria. */
function PendingSection() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [categoryDraft, setCategoryDraft] = useState<Record<string, string>>({});

  const { data: pending, isLoading } = useQuery({
    queryKey: ['pending'],
    queryFn: () => api.get<PendingTransaction[]>('/pending'),
  });
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<Category[]>('/categories'),
  });

  const fail = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : 'Operazione non riuscita');

  const confirm = useMutation({
    mutationFn: ({ id, categoryId }: { id: string; categoryId: string }) =>
      api.post(`/pending/${id}/confirm`, { categoryId }),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries();
    },
    onError: fail,
  });

  const discard = useMutation({
    mutationFn: (id: string) => api.delete(`/pending/${id}`),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['pending'] });
    },
    onError: fail,
  });

  if (isLoading || !pending || pending.length === 0) return null;

  return (
    <section>
      <SectionTitle>Da categorizzare ({pending.length})</SectionTitle>
      <Card className="flex flex-col gap-2 p-3">
        {error && (
          <p
            role="alert"
            className="rounded-[var(--radius-control)] bg-danger-soft px-3 py-2 text-sm text-danger"
          >
            {error}
          </p>
        )}
        {pending.map((p) => (
          <div
            key={p.id}
            className="flex items-center gap-2 rounded-[var(--radius-control)] bg-surface-2 px-3 py-2.5"
          >
            <span className="min-w-0 flex-1">
              <span className="block text-xs text-muted">
                {new Date(p.date).toLocaleDateString('it-IT')}
              </span>
            </span>
            <span className="tnum text-sm">{p.amount.toFixed(2)} €</span>
            <select
              value={categoryDraft[p.id] ?? p.categoryId}
              onChange={(e) => setCategoryDraft((d) => ({ ...d, [p.id]: e.target.value }))}
              aria-label={`Categoria per il movimento del ${new Date(p.date).toLocaleDateString('it-IT')}`}
              className="rounded-lg bg-surface px-2 py-1.5 text-sm"
            >
              <option value="">Categoria</option>
              {(categories ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <Button
              onClick={() =>
                confirm.mutate({ id: p.id, categoryId: categoryDraft[p.id] ?? p.categoryId })
              }
              disabled={!(categoryDraft[p.id] ?? p.categoryId) || confirm.isPending}
            >
              OK
            </Button>
            <button
              type="button"
              aria-label={`Scarta movimento del ${new Date(p.date).toLocaleDateString('it-IT')}`}
              onClick={() => discard.mutate(p.id)}
              className="text-muted"
            >
              ✕
            </button>
          </div>
        ))}
      </Card>
    </section>
  );
}
